import { A } from "@solidjs/router";
import { createSignal, Show, Switch, Match } from "solid-js";
import { AplombButton, AplombChip, AplombField } from "~/components";
import { AplombApiClient, type SubmitProApplicationRequest } from "~/lib/api/client";
import { captureEvent } from "~/lib/observability";
import "./signup.css";

interface FormState {
  email: string;
  businessName: string;
  legalName: string;
  neq: string;
  rbqNumber: string;
  cmmtqNumber: string;
  postalFsa: string;
  city: string;
  phone: string;
}

interface Validation {
  email?: string;
  businessName?: string;
  legalName?: string;
  neq?: string;
  rbqNumber?: string;
  cmmtqNumber?: string;
  postalFsa?: string;
  city?: string;
  phone?: string;
}

function validate(form: FormState): Validation {
  const errs: Validation = {};
  if (!form.email.includes("@")) errs.email = "Adresse courriel invalide";
  if (form.businessName.trim().length < 2) errs.businessName = "Nom d'entreprise requis";
  if (form.legalName.trim().length < 2) errs.legalName = "Raison sociale requise";
  if (!/^\d{10}$/.test(form.neq.trim()))
    errs.neq = "NEQ = 10 chiffres (Registraire des entreprises QC)";
  if (form.rbqNumber.trim().length < 3) errs.rbqNumber = "Numéro RBQ requis";
  if (form.cmmtqNumber.trim().length < 3) errs.cmmtqNumber = "Numéro CMMTQ requis";
  if (form.postalFsa.trim().length !== 3) errs.postalFsa = "FSA (3 caractères) requis";
  if (form.city.trim().length < 2) errs.city = "Ville requise";
  if (form.phone.replace(/\D/g, "").length < 10) errs.phone = "Téléphone à 10 chiffres";
  return errs;
}

const client = new AplombApiClient();

export default function ProSignup() {
  const [form, setForm] = createSignal<FormState>({
    email: "",
    businessName: "",
    legalName: "",
    neq: "",
    rbqNumber: "",
    cmmtqNumber: "",
    postalFsa: "",
    city: "Montréal",
    phone: "",
  });
  const [errors, setErrors] = createSignal<Validation>({});
  const [status, setStatus] = createSignal<
    | { kind: "idle" }
    | { kind: "submitting" }
    | { kind: "success"; applicationId: string; message: string }
    | { kind: "error"; title: string; detail: string }
  >({ kind: "idle" });

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm({ ...form(), [key]: value });
  };

  const onSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    const v = validate(form());
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setStatus({ kind: "submitting" });
    void captureEvent("pro_signup_submit", { postal_fsa: form().postalFsa });

    const payload: SubmitProApplicationRequest = {
      email: form().email.trim(),
      business_name: form().businessName.trim(),
      legal_name: form().legalName.trim(),
      neq: form().neq.trim(),
      rbq_number: form().rbqNumber.trim(),
      cmmtq_number: form().cmmtqNumber.trim(),
      postal_code_prefix: form().postalFsa.trim().toUpperCase(),
      city: form().city.trim(),
      phone: form().phone.trim(),
    };

    const result = await client.submitProApplication(payload);
    if (result.ok) {
      void captureEvent("pro_signup_success", {
        application_id: result.value.application_id,
      });
      setStatus({
        kind: "success",
        applicationId: result.value.application_id,
        message: result.value.message,
      });
    } else {
      void captureEvent("pro_signup_error", {
        status: result.status,
        title: result.title,
      });
      setStatus({ kind: "error", title: result.title, detail: result.detail });
    }
  };

  return (
    <>
      <header class="a-nav">
        <div class="a-container a-nav__inner">
          <A href="/" class="a-nav__brand">
            <span class="a-nav__brandmark">A|</span>
            <span class="a-nav__brandtext">Aplomb</span>
          </A>
          <A href="/login" class="a-nav__back">Se connecter</A>
        </div>
      </header>

      <main class="a-container a-signup">
        <Switch>
          <Match when={status().kind === "success"}>
            {(() => {
              const s = status() as Extract<ReturnType<typeof status>, { kind: "success" }>;
              return (
                <section class="a-signup__success">
                  <AplombChip variant="conforming">Candidature reçue</AplombChip>
                  <h1>Merci — votre candidature est enregistrée.</h1>
                  <p>
                    Numéro de suivi : <strong>{s.applicationId}</strong>
                  </p>
                  <p>{s.message}</p>
                  <div class="a-signup__nextsteps">
                    <h3>Prochaines étapes</h3>
                    <ol>
                      <li>Vérification de vos licences (RBQ, CMMTQ, CCQ, GCR) — 3-7 jours ouvrables</li>
                      <li>Appel de 3 références client + entrevue vidéo 1h avec un fondateur Aplomb</li>
                      <li>Essai pratique sur un projet pilote encadré (payé, supervisé)</li>
                      <li>Décision d'activation ou refus motivé — 6 semaines maximum</li>
                    </ol>
                    <p class="a-signup__meta">
                      Voir <A href="/docs/pro/screening">documentation screening 10 points</A>.
                    </p>
                  </div>
                </section>
              );
            })()}
          </Match>

          <Match when={status().kind !== "success"}>
            <section class="a-signup__form">
              <AplombChip variant="attention" compact>
                Screening humain — 6 semaines
              </AplombChip>
              <h1>Devenir plombier vérifié Aplomb</h1>
              <p class="a-signup__intro">
                Aucun frais pour poser sa candidature. Aucun frais tant que vous n'êtes pas activé.
                À l'activation : abonnement 79 $/mois + commission 6,5 % HT + caution 500 $ (récupérable).
              </p>

              <form onSubmit={onSubmit} novalidate>
                <fieldset disabled={status().kind === "submitting"}>
                  <div class="a-signup__grid">
                    <AplombField
                      label="Courriel de contact"
                      type="email"
                      required
                      autocomplete="email"
                      value={form().email}
                      onInput={(e) => update("email", e.currentTarget.value)}
                      error={errors().email}
                    />
                    <AplombField
                      label="Téléphone"
                      type="tel"
                      required
                      autocomplete="tel-national"
                      value={form().phone}
                      onInput={(e) => update("phone", e.currentTarget.value)}
                      error={errors().phone}
                      helper="514 555-0142"
                    />
                    <AplombField
                      label="Nom d'entreprise commercial"
                      required
                      autocomplete="organization"
                      value={form().businessName}
                      onInput={(e) => update("businessName", e.currentTarget.value)}
                      error={errors().businessName}
                    />
                    <AplombField
                      label="Raison sociale (ex : 9999-9999 Québec inc.)"
                      required
                      value={form().legalName}
                      onInput={(e) => update("legalName", e.currentTarget.value)}
                      error={errors().legalName}
                    />
                    <AplombField
                      label="NEQ (10 chiffres)"
                      type="neq"
                      required
                      value={form().neq}
                      onInput={(e) => update("neq", e.currentTarget.value)}
                      error={errors().neq}
                      helper="Registraire des entreprises Québec"
                    />
                    <AplombField
                      label="Numéro RBQ (catégorie 15 — plomberie)"
                      required
                      value={form().rbqNumber}
                      onInput={(e) => update("rbqNumber", e.currentTarget.value)}
                      error={errors().rbqNumber}
                    />
                    <AplombField
                      label="Numéro CMMTQ (membre actif)"
                      required
                      value={form().cmmtqNumber}
                      onInput={(e) => update("cmmtqNumber", e.currentTarget.value)}
                      error={errors().cmmtqNumber}
                    />
                    <AplombField
                      label="FSA (3 premiers caractères code postal siège social)"
                      type="postal-fsa"
                      required
                      autocomplete="postal-code"
                      value={form().postalFsa}
                      onInput={(e) => update("postalFsa", e.currentTarget.value.toUpperCase())}
                      error={errors().postalFsa}
                    />
                    <AplombField
                      label="Ville"
                      required
                      autocomplete="address-level2"
                      value={form().city}
                      onInput={(e) => update("city", e.currentTarget.value)}
                      error={errors().city}
                    />
                  </div>

                  <Show when={status().kind === "error"}>
                    {(() => {
                      const s = status() as Extract<ReturnType<typeof status>, { kind: "error" }>;
                      return (
                        <div class="a-urgency__error" role="alert">
                          <strong>{s.title}</strong>
                          <p>{s.detail}</p>
                        </div>
                      );
                    })()}
                  </Show>

                  <AplombButton
                    variant="primary"
                    size="large"
                    type="submit"
                    fullWidth
                    loading={status().kind === "submitting"}
                  >
                    Soumettre ma candidature
                  </AplombButton>

                  <p class="a-signup__legal">
                    Vos informations sont utilisées uniquement pour la vérification 10 points.
                    Voir <A href="/confidentialite">politique de confidentialité</A> (Loi 25).
                  </p>
                </fieldset>
              </form>
            </section>
          </Match>
        </Switch>
      </main>
    </>
  );
}
